import { useEffect } from 'react';
import { SITE } from '../config/seo';

function upsertMeta(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMetaByName(name, content) {
  if (content == null) return;
  const el = upsertMeta(`meta[name="${name}"]`, () => {
    const node = document.createElement('meta');
    node.setAttribute('name', name);
    return node;
  });
  el.setAttribute('content', content);
}

function setMetaByProperty(property, content) {
  if (content == null) return;
  const el = upsertMeta(`meta[property="${property}"]`, () => {
    const node = document.createElement('meta');
    node.setAttribute('property', property);
    return node;
  });
  el.setAttribute('content', content);
}

function setCanonical(url) {
  const el = upsertMeta('link[rel="canonical"]', () => {
    const node = document.createElement('link');
    node.setAttribute('rel', 'canonical');
    return node;
  });
  el.setAttribute('href', url);
}

export function usePageSeo({
  title = SITE.title,
  description = SITE.description,
  path = '/',
  index = true,
  jsonLd = null,
}) {
  useEffect(() => {
    const url = `${SITE.url}${path === '/' ? '' : path}`;

    document.title = title;
    setMetaByName('description', description);
    setMetaByName('keywords', SITE.keywords);
    setMetaByName('robots', index ? 'index, follow' : 'noindex, nofollow');
    setMetaByName('author', SITE.name);
    setMetaByName('twitter:card', 'summary_large_image');
    setMetaByName('twitter:title', title);
    setMetaByName('twitter:description', description);
    setMetaByName('twitter:image', SITE.ogImage);
    if (SITE.twitterHandle) {
      setMetaByName('twitter:site', SITE.twitterHandle);
    }

    setMetaByProperty('og:type', 'website');
    setMetaByProperty('og:site_name', SITE.name);
    setMetaByProperty('og:title', title);
    setMetaByProperty('og:description', description);
    setMetaByProperty('og:url', url);
    setMetaByProperty('og:image', SITE.ogImage);
    setMetaByProperty('og:locale', 'en_US');

    setCanonical(url);

    const scriptId = 'flowfence-json-ld';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();

    if (jsonLd) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      const payload = Array.isArray(jsonLd) && jsonLd.length > 1
        ? { '@context': 'https://schema.org', '@graph': jsonLd }
        : (Array.isArray(jsonLd) ? jsonLd[0] : jsonLd);
      script.text = JSON.stringify(payload);
      document.head.appendChild(script);
    }

    return () => {
      const script = document.getElementById(scriptId);
      if (script) script.remove();
    };
  }, [title, description, path, index, jsonLd]);
}
